import * as React from 'react';
import { reactionsDriverFactory, ReactionsDriver } from './Reactions.driver';
import { createRendererWithUniDriver } from '../../test/utils';
import { Reactions } from '.';
import { TPAComponentsProvider } from '../TPAComponentsConfig';
import { heartItemsData, heartItems } from './fixtures';
import { ReactionType } from './types';
import { actDelay, getFocus } from '../../test/helpers';

const defProps: any = {
  'data-hook': 'data-hook',
  onReactionSelect: () => {},
  additionalReactions: heartItems,
  reactionIcon: (id: ReactionType) => heartItemsData[id].icon,
  reactionLabel: (id: ReactionType) => heartItemsData[id].label,
  defaultReaction: 'heart',
  popoverTriggerAriaLabel: 'Reactions',
};

describe('Reactions', () => {
  const render = createRendererWithUniDriver<ReactionsDriver>(
    reactionsDriverFactory,
  );

  it('should render', async () => {
    const { driver } = render(
      <Reactions {...defProps} />,
      defProps['data-hook'],
    );
    expect(await driver.exists()).toBe(true);
  });

  it('should return correct reaction id on reaction click', async () => {
    const onReactionSelect = jest.fn();
    const { driver } = render(
      <Reactions {...defProps} onReactionSelect={onReactionSelect} />,
      defProps['data-hook'],
    );
    await driver.openReactionList();

    // wait for reaction list to open
    await actDelay(1000);

    await driver.clickReactionById(heartItems[1]);

    expect(onReactionSelect).toHaveBeenCalledWith(heartItems[1]);

    await driver.openReactionList();

    // wait for reaction list to open
    await actDelay(1000);

    await driver.clickReactionById(heartItems[0]);

    expect(onReactionSelect).toHaveBeenCalledWith(heartItems[0]);
  });

  it('should show correct label when passing selectedId prop', async () => {
    const onReactionSelect = jest.fn();
    const { driver } = render(
      <Reactions
        {...defProps}
        onReactionSelect={onReactionSelect}
        selectedId={heartItems[2]}
      />,
      defProps['data-hook'],
    );

    expect(await driver.getReactionLabel()).toEqual(
      heartItemsData[heartItems[2]].label,
    );
  });

  it('should open reaction list on hover', async () => {
    const { driver } = render(
      <Reactions {...defProps} />,
      defProps['data-hook'],
    );
    await driver.hoverTriggerButton();

    // wait for reaction list to open
    await actDelay(1000);

    expect(await driver.isOpen()).toBe(true);
  });

  it('should open reaction list only after clicking trigger button on mobile', async () => {
    const { driver } = render(
      <TPAComponentsProvider value={{ mobile: true }}>
        <Reactions {...defProps} />
      </TPAComponentsProvider>,
      defProps['data-hook'],
    );

    await driver.hoverTriggerButton();

    // wait for reaction list to open
    await actDelay(1000);

    expect(await driver.isOpen()).toBe(false);

    await driver.clickTriggerButton();

    await actDelay(1000);

    expect(await driver.isOpen()).toBe(true);
  });

  it('should close popover and focus on last active element', async () => {
    const { driver } = render(
      <Reactions {...defProps} />,
      defProps['data-hook'],
    );

    await driver.triggerButton.focus();
    await driver.subTriggerButton.focus();
    await driver.subTriggerButton.click();

    const lastFocused = await driver.focused.element();

    // wait for reaction list to open
    await actDelay(1000);

    expect(await driver.focused.element()).not.toBe(lastFocused);

    await driver.popover._base.pressKey('Escape');

    // wait for reaction list to close
    await actDelay(1000);

    expect(await driver.focused.element()).toBe(lastFocused);
  });

  it('should open popover and have first reaction focused', async () => {
    const { driver } = render(
      <Reactions {...defProps} />,
      defProps['data-hook'],
    );

    await driver.triggerButton.focus();
    await driver.subTriggerButton.click();

    // wait for reaction list to open
    await actDelay(1000);

    expect(await driver.focused.attr('data-id')).toBe('heart');
  });

  it('should change focus using arrow keys', async () => {
    const { driver } = render(
      <Reactions {...defProps} />,
      defProps['data-hook'],
    );

    await driver.triggerButton.focus();
    await driver.subTriggerButton.click();

    // wait for reaction list to open
    await actDelay(1000);

    expect(await driver.focused.attr('data-id')).toBe('heart');

    expect(await driver.focused.element()).toBe(getFocus());
    await driver.focused.pressKey('ArrowRight');

    // wait for re-render
    await actDelay(1000);

    expect(await driver.focused.attr('data-id')).toBe(heartItems[0]);

    expect(await driver.focused.element()).toBe(getFocus());
    await driver.focused.pressKey('ArrowLeft');

    // wait for re-render
    await actDelay(1000);

    expect(await driver.focused.attr('data-id')).toBe('heart');
  });
});
